<template>
  <div class="temporary-candidate-root">
    <QHeader class="bg-white text-primary">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="onGoBack()" />
        <QToolbarTitle>
          {{ $t("ETAB.PAGES.EXTRA.@TITLE") }}
        </QToolbarTitle>
        <QBtn
          v-if="canDeclareProblem"
          class="q-pa-xs"
          outline
          color="secondary"
          no-caps
          dense
          icon-right="warning_amber"
          :label="$t('ETAB.PAGES.RECRUITMENT.@REPORT_PROBLEM')"
          @click="onReportProblem"
        />
      </QToolbar>
      <QTabs v-if="hasMissionTabs" dense align="justify">
        <QRouteTab no-caps :to="missionRoute" :label="$t('ETAB.PAGES.EXTRA.@MISSION_TAB')" />
        <QRouteTab no-caps :to="profileRoute" :label="$t('ETAB.PAGES.EXTRA.@PROFILE_TAB')" />
      </QTabs>
    </QHeader>

    <QPageContainer>
      <QPage>
        <router-view />
      </QPage>
    </QPageContainer>

    <QFooter class="bg-transparent">
      <FooterBtnsWrapper>
        <ExtraBtnIcon
          v-if="canSelect && !recruitment.isBookingExpress()"
          class="full-width"
          color="secondary"
          type="button"
          no-caps
          :disable="$_isGlobalLoadingActive"
          :all-space="true"
          icon-name="icon-extra-magic"
          :btn-label="$t('ETAB.PAGES.EXTRA.@SELECT_EXTRA')"
          @click="onSelectExtra"
        />

        <QBanner v-if="extraHasCanceled" class="full-width bg-grey text-center text-bold">
          <span class="text-h3">{{ $t("ETAB.PAGES.EXTRA.@CANCELED_EXTRA") }}</span>
        </QBanner>
      </FooterBtnsWrapper>
      <Contact v-if="canAccessContactInformations" :phone="extra.getIdentity().phone" :email="extra.getIdentity().email" />
    </QFooter>

    <IconTextConfirmModal
      v-model="problemModalOpened"
      :backdrop-dismiss="true"
      :on-confirm-modal="declareExtraMissingMER"
      :on-close-modal="contactEtabSupport"
      icon="icon-f-alert-octagon"
      :confirm="$t('ETAB.PAGES.EXTRA.REPORT_PROBLEM.@ACTION_DECLARE_MISSING')"
      :refuse="$t('ETAB.PAGES.EXTRA.REPORT_PROBLEM.@ACTION_CONTACT_SUPPORT')"
      :text="$t('ETAB.PAGES.EXTRA.REPORT_PROBLEM.@TITLE', { name: extra.getIdentity().firstname })"
      confirm-color="secondary"
      refuse-color="primary-medium"
    />

    <ExtraSelectedModal v-model:opened="successModalOpened" />
    <ExtraGoCardlessModal
      v-model:opened="goCardlessModalOpened"
      :has-epct-feature-flag="profile.hasEpctFeatureFlag()"
      @on-click-iban-btn="openIbanLink"
      @on-click-already-done="onClickAlreadyDone"
    />
  </div>
</template>

<script>
  import { JavascriptHelpers } from "@core/helpers";
  import { ContactManager } from "@core/managers/contact.manager";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { EtabProfileService, ExtraService, RecruitmentCandidatesService } from "@core/services";
  import { IconTextConfirmModal } from "@core/components/modals";
  import { GO_CARDLESS } from "@core/constants";
  import { ETAB_ROUTES } from "@etab/config";
  import {
    GETTER_RECRUITMENT_GET_CURRENT,
    GETTER_EXTRA_GET_CURRENT,
    ACTION_EXTRA_GET_BY_ID,
    ACTION_RECRUITMENT_GET,
    GETTER_PROFILE_GET,
  } from "@etab/stores/types";
  import { useExtraStore } from "@etab/stores/extra";
  import { useProfileStore } from "@etab/stores/profile";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { mapStores } from "pinia";

  import Contact from "@etab/components/common/Contact.component.vue";
  import ExtraSelectedModal from "@etab/components/extra/ExtraSelected.modal.vue";
  import ExtraGoCardlessModal from "@etab/components/extra/GoCardless.modal.vue";

  export default {
    components: {
      Contact,
      ExtraSelectedModal,
      IconTextConfirmModal,
      ExtraGoCardlessModal,
    },
    data() {
      return {
        successModalOpened: false,
        problemModalOpened: false,
        goCardlessModalOpened: false,
      };
    },
    computed: {
      ...mapStores(useProfileStore, useExtraStore, useRecruitmentStore),
      profile() {
        return this.profileStore[GETTER_PROFILE_GET];
      },
      demandId() {
        return this.$route.params.demandId;
      },
      linkedId() {
        return this.$route.params.linkedId;
      },
      demandType() {
        return this.$route.params.demandType;
      },
      candidateId() {
        return this.$route.params.candidateId;
      },
      recruitment() {
        return this.recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
      },
      extra() {
        return this.extraStore[GETTER_EXTRA_GET_CURRENT];
      },
      isExtraBooked() {
        return this.extra.isSelected();
      },
      extraHasCanceled() {
        return this.extra.isCanceled();
      },
      hasMissionTabs() {
        return this.recruitment.isAE() && (this.isExtraBooked || this.extraHasCanceled);
      },
      canAccessContactInformations() {
        return ExtraService.canAccessContactInformations({ extra: this.extra, recruitment: this.recruitment });
      },
      canDeclareProblem() {
        return (
          this.recruitment.hasStarted() && //
          !this.recruitment.hasEnded() &&
          this.isExtraBooked &&
          !this.extraHasCanceled
        );
      },
      canSelect() {
        return (
          !this.recruitment.hasEnded() && //
          !this.isExtraBooked &&
          !this.extraHasCanceled
        );
      },
    },
    created() {
      ScrollManager.scrollToTop();
      this.initRoutes();
    },
    mounted() {
      // stop loading started in routing
      this.$_stopGlobalLoading();
    },
    methods: {
      initRoutes() {
        const demandId = this.demandId;
        const demandType = this.demandType;
        const candidateId = this.candidateId;

        this.missionRoute = {
          name: ETAB_ROUTES.dashboard.recruitment.temporary.candidate.mission.name,
          params: { demandId, demandType, candidateId },
        };
        this.profileRoute = {
          name: ETAB_ROUTES.dashboard.recruitment.temporary.candidate.profile.name,
          params: { demandId, demandType, candidateId },
        };
        this.recruitmentDetailRoute = {
          name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.root.name,
          params: { demandId, demandType },
        };
        this.appliedCandidatesRoute = {
          name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.applied.name,
          params: { demandId, demandType },
        };
        this.selectedCandidatesRoute = {
          name: ETAB_ROUTES.dashboard.recruitment.temporary.detail.selected.name,
          params: { demandId, demandType },
        };
      },
      openSuccessModal() {
        this.successModalOpened = true;
      },
      openGoCardlessModal() {
        this.goCardlessModalOpened = true;
      },
      openProblemModal() {
        this.problemModalOpened = true;
      },
      openIbanLink() {
        if (this.profile.hasEpctFeatureFlag()) {
          this.$router.push({ name: ETAB_ROUTES.payment.iban.name, query: { url: this.$route.path } }).catch((suppressedError) => {});
        } else {
          JavascriptHelpers.openUrl(GO_CARDLESS, "_blank");
        }
      },
      async onClickAlreadyDone() {
        const message = "WARNINGS.@GO_CARDLESS_PLEASE_WAIT";
        const color = "warning";
        const etabId = await EtabProfileService.getEtabId();
        const subject = this.$t("EMAILS.@GO_CARDLESS_EMAIL_SUBJECT", { id: etabId });

        NotificationsManager.contactEtabSupport({ message, color, subject });
      },
      async refresh({ displayLoading = true } = {}) {
        try {
          if (displayLoading) {
            this.$_startGlobalLoading();
          }
          const demandId = this.demandId;
          const demandType = this.demandType;
          const type = this.recruitment.getType();
          const extraId = this.extra.getId();
          const linkedId = this.linkedId;
          await this.recruitmentStore[ACTION_RECRUITMENT_GET]({ demandId, demandType });
          await this.extraStore[ACTION_EXTRA_GET_BY_ID]({ extraId, demandId: linkedId || demandId, type });
        } catch (error) {
          const message = (error && error.message) || "ERRORS.@SOMETHING_WENT_WRONG";
          NotificationsManager.error({ message });
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      onGoBack() {
        if (this.isExtraBooked || this.extraHasCanceled) {
          return this.$router.push(this.selectedCandidatesRoute).catch((suppressedError) => {});
        }
        return this.$router.push(this.appliedCandidatesRoute).catch((suppressedError) => {});
      },
      onSelectExtra() {
        if (this.recruitment.isAE()) {
          this.selectExtraAE();
        }
      },
      async selectExtraAE() {
        const demandId = this.demandId;
        const extraId = this.extra.getId();
        const extraName = this.extra.getIdentity().firstname;
        const recruitment = this.recruitment;
        try {
          this.$_startGlobalLoading();
          // we try to book regardless of the status of allowedAE
          await RecruitmentCandidatesService.bookExtraAE({ recruitment, extraId });
          await this.refresh({ displayLoading: false });
          this.openSuccessModal();
        } catch (error) {
          const response = error?.request?.response;
          const status = error?.request?.status;
          switch (status) {
            case 400: // validation error
              NotificationsManager.error({ message: response });
              break;
            case 412: // extra not available
              NotificationsManager.error({ message: "ERRORS.@EXTRA_NOT_AVAILABLE" });
              break;
            case 456:
              // status code 456 means the etab is not allowed to book AE
              // the etab needs to register his IBAN with Go cardless or Stripe
              // here, depending on FEATURE FLAG EPCT, either go cardless or redirect to IBAN form
              this.openGoCardlessModal();
              break;
            default:
              // whatever happens outside of documented cases, we offer the possibility to contact support
              NotificationsManager.contactEtabSupport({ subject: this.$t("EMAILS.@ETAB_CONTACT_EXTRA_PROBLEM", { demandId, extraName, extraId }) });
              break;
          }
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async declareExtraMissingMER() {
        try {
          this.$_startGlobalLoading();
          await RecruitmentCandidatesService.declareExtraMissing({ recruitment: this.recruitment, id: this.linkedId });
          await this.refresh();
          this.onGoBack();
        } catch (error) {
          this.$_stopGlobalLoading();
          NotificationsManager.contactEtabSupport();
          throw error;
        } finally {
          // we stopGlobalLoading in the refresh method
        }
      },
      onReportProblem() {
        this.contactEtabSupport();
      },
      contactEtabSupport() {
        const demandId = this.demandId;
        const candidateId = this.candidateId;
        const extraName = this.extra.getIdentity().firstname;
        const subject = this.$t("EMAILS.@DEMAND_PROBLEM_EXTRA", { demandId, extraName, extraId: candidateId });
        ContactManager.contactEtabSupport({ subject });
      },
    },
  };
</script>

<style lang="scss">
  .temporary-candidate-root {
    background-color: $body-background-light-grey;

    .q-page {
      overflow: hidden; // remove the scroll during transition
      padding: 0 15px 15px;
    }
  }
</style>
